import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button } from 'react-bootstrap';
import { AttachedSprayInstruction } from '../../services/sprayService';
import { formatDateTime } from '../../dateFormat';
import ViewInstructionAnchor from './ViewInstructionAnchor';

/**
 * Single row inside the "Attached Instructions" section of the spray
 * session detail view. Extracted from SpraySessionDetail.tsx (Phase C2,
 * Task 3.6) to make the row + its View-PDF flow independently testable.
 *
 * Behaviour preserved verbatim from the inline implementation:
 * - Left side renders the createdAt timestamp humanised in the tenant
 *   timezone, plus the "by {{name}}" subtitle when a createdBy is present
 *   (name → username fallback handled by `renderCreatedBy`).
 * - Right side renders, in order: an optional "PDF unavailable" badge
 *   (when `pdfKey` is missing), the View anchor, and a Bootstrap-styled
 *   Delete button. The Delete button uses `data-action="delete"` so the
 *   parent's existing tests can target it without ambiguity.
 * - Outer wrapper keeps the `data-testid="attached-instruction-row"` and
 *   `data-instruction-id={inst._id}` selectors used throughout the
 *   detail-view tests.
 */
export interface AttachedInstructionRowProps {
  instruction: AttachedSprayInstruction;
  farmTimezone?: string;
  fetchSignedUrl: (id: string) => Promise<string>;
  onDelete: (instruction: AttachedSprayInstruction) => void;
  onViewError?: (message: string) => void;
}

const renderCreatedBy = (cb: AttachedSprayInstruction['createdBy']): string => {
  if (!cb) return '';
  if (typeof cb === 'string') return cb;
  return cb.name || cb.username || '';
};

const AttachedInstructionRow: React.FC<AttachedInstructionRowProps> = ({
  instruction,
  farmTimezone,
  fetchSignedUrl,
  onDelete,
  onViewError,
}) => {
  const { t } = useTranslation();
  const hasPdf = !!instruction.pdfKey;
  const by = renderCreatedBy(instruction.createdBy);

  return (
    <div
      data-testid="attached-instruction-row"
      data-instruction-id={instruction._id}
      className="d-flex justify-content-between align-items-center border rounded p-2 mb-2"
    >
      <div>
        <div>{formatDateTime(instruction.createdAt, { timeZone: farmTimezone })}</div>
        {by && (
          <small className="text-muted">
            {t('spray.sessions.byUser', { name: by })}
          </small>
        )}
      </div>
      <div className="d-flex gap-2 align-items-center">
        {!hasPdf && (
          <Badge bg="warning" text="dark">
            {t('spray.sessions.pdfUnavailable')}
          </Badge>
        )}
        <ViewInstructionAnchor
          instructionId={instruction._id}
          hasPdf={hasPdf}
          fetchSignedUrl={fetchSignedUrl}
          onError={onViewError}
        />
        <Button
          variant="outline-danger"
          size="sm"
          data-action="delete"
          onClick={() => onDelete(instruction)}
        >
          {t('common.delete')}
        </Button>
      </div>
    </div>
  );
};

export default AttachedInstructionRow;
